/**
 * Utilidades para impresión térmica
 * Compatible con impresoras térmicas de 58mm y 80mm
 */

/**
 * Genera el contenido HTML para impresión térmica
 */
export const generateThermalReceipt = (recoleccion) => {
  if (!recoleccion) {
    console.error('No se proporcionó una recolección para imprimir');
    return '';
  }

  const fecha = new Date().toLocaleString('es-CO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        @page {
          size: 80mm auto;
          margin: 0;
        }
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: 'Courier New', monospace;
          font-size: 12px;
          line-height: 1.4;
          padding: 10px;
          width: 80mm;
          background: white;
        }
        
        .header {
          text-align: center;
          margin-bottom: 15px;
          border-bottom: 2px dashed #000;
          padding-bottom: 10px;
        }
        
        .header h1 {
          font-size: 18px;
          font-weight: bold;
          margin-bottom: 5px;
        }
        
        .header h2 {
          font-size: 14px;
          margin-bottom: 3px;
        }
        
        .consecutivo {
          text-align: center;
          font-size: 16px;
          font-weight: bold;
          margin: 10px 0;
          padding: 5px;
          border: 2px solid #000;
        }
        
        .info-section {
          margin: 10px 0;
          font-size: 11px;
        }
        
        .info-row {
          display: flex;
          justify-content: space-between;
          margin: 3px 0;
        }
        
        .info-label {
          font-weight: bold;
        }
        
        .divider {
          border-top: 1px dashed #000;
          margin: 10px 0;
        }
        
        .items-header {
          font-weight: bold;
          font-size: 12px;
          margin: 10px 0 5px 0;
          text-align: center;
          border-top: 2px solid #000;
          border-bottom: 2px solid #000;
          padding: 5px 0;
        }
        
        .item {
          margin: 5px 0;
          padding: 5px 0;
          border-bottom: 1px dotted #ccc;
        }
        
        .item-name {
          font-weight: bold;
          font-size: 12px;
        }
        
        .item-details {
          font-size: 10px;
          color: #333;
          margin-top: 2px;
        }
        
        .item-qty {
          text-align: right;
          font-size: 14px;
          font-weight: bold;
        }
        
        .total-section {
          margin-top: 15px;
          padding-top: 10px;
          border-top: 2px solid #000;
          text-align: center;
        }
        
        .total {
          font-size: 16px;
          font-weight: bold;
          margin: 5px 0;
        }
        
        .footer {
          text-align: center;
          margin-top: 15px;
          padding-top: 10px;
          border-top: 2px dashed #000;
          font-size: 10px;
        }
        
        .signature {
          margin-top: 30px;
          text-align: center;
        }
        
        .signature-line {
          border-top: 1px solid #000;
          width: 60%;
          margin: 0 auto 5px auto;
        }
        
        @media print {
          body {
            width: 80mm;
          }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>SANIMAX</h1>
        <h2>Depósito de Ropa</h2>
        <div>${recoleccion.planta?.nombre || 'Planta'}</div>
      </div>
      
      <div class="consecutivo">
        ${recoleccion.consecutivo || 'N/A'}
      </div>
      
      <div class="info-section">
        <div class="info-row">
          <span class="info-label">Fecha:</span>
          <span>${fecha}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Empleado:</span>
          <span>${recoleccion.empleado?.nombre || 'N/A'}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Cédula:</span>
          <span>${recoleccion.empleado?.cedula || 'N/A'}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Área:</span>
          <span>${recoleccion.empleado?.area?.nombre || 'N/A'}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Estado:</span>
          <span>${getEstadoLabel(recoleccion.estado)}</span>
        </div>
      </div>
      
      <div class="items-header">
        PRENDAS RECIBIDAS
      </div>
      
      ${recoleccion.prendas?.map((prenda, index) => `
        <div class="item">
          <div style="display: flex; justify-content: space-between; align-items: start;">
            <div style="flex: 1;">
              <div class="item-name">${index + 1}. ${prenda.nombre}</div>
              ${prenda.descripcion ? `<div class="item-details">Desc: ${prenda.descripcion}</div>` : ''}
              ${prenda.observaciones ? `<div class="item-details">Obs: ${prenda.observaciones}</div>` : ''}
            </div>
            <div class="item-qty">x${prenda.cantidad}</div>
          </div>
        </div>
      `).join('') || '<div class="item">No hay prendas registradas</div>'}
      
      <div class="total-section">
        <div class="total">
          TOTAL PRENDAS: ${recoleccion.cantidad_prendas || 0}
        </div>
      </div>
      
      <div class="signature">
        <div class="signature-line"></div>
        <div>Firma del Empleado</div>
      </div>
      
      <div class="footer">
        <div>Recibido por: ${recoleccion.creator?.nombre || 'Sistema'}</div>
        <div style="margin-top: 10px;">Gracias por su confianza</div>
        <div style="margin-top: 5px;">www.sanimax.com</div>
      </div>
    </body>
    </html>
  `;
};

/**
 * Obtiene la etiqueta del estado en español
 */
const getEstadoLabel = (estado) => {
  const estados = {
    'recibido': 'RECIBIDO',
    'pendiente_verificacion': 'PENDIENTE VERIFICACIÓN',
    'verificado': 'VERIFICADO',
    'rechazado': 'RECHAZADO',
    'entregado': 'ENTREGADO'
  };
  return estados[estado] || estado?.toUpperCase() || 'N/A';
};

/**
 * Imprime el recibo en una impresora térmica
 */
export const printThermalReceipt = (recoleccion) => {
  const content = generateThermalReceipt(recoleccion);
  
  // Crear un iframe oculto para la impresión
  const iframe = document.createElement('iframe');
  iframe.style.position = 'absolute';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = 'none';
  
  document.body.appendChild(iframe);
  
  const iframeDoc = iframe.contentWindow.document;
  iframeDoc.open();
  iframeDoc.write(content);
  iframeDoc.close();
  
  // Esperar a que se cargue el contenido
  iframe.onload = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      
      // Remover el iframe después de imprimir
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    } catch (error) {
      console.error('Error al imprimir:', error);
      document.body.removeChild(iframe);
    }
  };
};

/**
 * Genera un preview del recibo para visualización
 */
export const previewThermalReceipt = (recoleccion) => {
  const content = generateThermalReceipt(recoleccion);
  const previewWindow = window.open('', '_blank', 'width=400,height=600');
  
  if (previewWindow) {
    previewWindow.document.write(content);
    previewWindow.document.close();
  }
};

/**
 * Genera el contenido HTML para recibo de entrega
 */
export const generateEntregaReceipt = (entregaData) => {
  if (!entregaData || !entregaData.empleado || !entregaData.prendas) {
    console.error('Datos de entrega incompletos');
    return '';
  }

  const fecha = new Date().toLocaleString('es-CO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const totalPrendas = entregaData.prendas.reduce((sum, p) => sum + (p.cantidad || 0), 0);

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        @page {
          size: 80mm auto;
          margin: 0;
        }
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: 'Courier New', monospace;
          font-size: 12px;
          line-height: 1.4;
          padding: 10px;
          width: 80mm;
          background: white;
        }
        
        .header {
          text-align: center;
          margin-bottom: 15px;
          border-bottom: 2px dashed #000;
          padding-bottom: 10px;
        }
        
        .company-name {
          font-size: 18px;
          font-weight: bold;
          margin-bottom: 5px;
        }
        
        .subtitle {
          font-size: 11px;
          margin-bottom: 3px;
        }
        
        .receipt-type {
          font-size: 16px;
          font-weight: bold;
          margin: 10px 0;
          text-align: center;
          background: #000;
          color: #fff;
          padding: 5px;
        }
        
        .info-section {
          margin: 10px 0;
          border-bottom: 1px dashed #000;
          padding-bottom: 10px;
        }
        
        .info-row {
          display: flex;
          justify-content: space-between;
          margin: 3px 0;
        }
        
        .label {
          font-weight: bold;
        }
        
        .items-section {
          margin: 10px 0;
        }
        
        .item {
          margin: 8px 0;
          padding: 5px 0;
          border-bottom: 1px dotted #ccc;
        }
        
        .item-name {
          font-weight: bold;
          font-size: 13px;
        }
        
        .item-details {
          font-size: 11px;
          color: #333;
          margin-top: 2px;
        }
        
        .item-qty {
          text-align: right;
          font-weight: bold;
        }
        
        .total-section {
          margin: 15px 0;
          padding: 10px;
          background: #f0f0f0;
          border: 2px solid #000;
          text-align: center;
        }
        
        .total-label {
          font-size: 14px;
          font-weight: bold;
        }
        
        .total-value {
          font-size: 20px;
          font-weight: bold;
          margin-top: 5px;
        }
        
        .signature-section {
          margin: 20px 0;
          text-align: center;
        }
        
        .signature-line {
          border-top: 1px solid #000;
          margin: 30px 20px 5px 20px;
        }
        
        .footer {
          text-align: center;
          margin-top: 15px;
          font-size: 11px;
          border-top: 2px dashed #000;
          padding-top: 10px;
        }
        
        @media print {
          body {
            width: 80mm;
          }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="company-name">SANIMAX</div>
        <div class="subtitle">Lavandería Proflux</div>
        <div class="subtitle">${entregaData.planta?.nombre || 'Planta'}</div>
      </div>
      
      <div class="receipt-type">COMPROBANTE DE ENTREGA</div>
      <div style="text-align: center; font-size: 14px; font-weight: bold; margin: 5px 0;">
        ${entregaData.consecutivo || 'ENT-000000'}
      </div>
      
      <div class="info-section">
        <div class="info-row">
          <span class="label">Fecha:</span>
          <span>${fecha}</span>
        </div>
        <div class="info-row">
          <span class="label">Empleado:</span>
          <span>${entregaData.empleado.nombre || 'N/A'}</span>
        </div>
        <div class="info-row">
          <span class="label">Cédula:</span>
          <span>${entregaData.empleado.cedula || 'N/A'}</span>
        </div>
        <div class="info-row">
          <span class="label">Área:</span>
          <span>${entregaData.empleado.area?.nombre || 'N/A'}</span>
        </div>
      </div>
      
      <div class="items-section">
        <div style="font-weight: bold; margin-bottom: 10px; text-align: center;">
          PRENDAS ENTREGADAS
        </div>
        ${entregaData.prendas.map((prenda, index) => `
          <div class="item">
            <div style="display: flex; justify-content: space-between; align-items: start;">
              <div style="flex: 1;">
                <div class="item-name">${index + 1}. ${prenda.nombre}</div>
                ${prenda.descripcion ? `<div class="item-details">Desc: ${prenda.descripcion}</div>` : ''}
                ${prenda.observaciones ? `<div class="item-details">Obs: ${prenda.observaciones}</div>` : ''}
                ${prenda.recoleccionConsecutivo ? `<div class="item-details">Recolección: ${prenda.recoleccionConsecutivo}</div>` : ''}
              </div>
              <div class="item-qty">x${prenda.cantidad || 1}</div>
            </div>
          </div>
        `).join('')}
      </div>
      
      <div class="total-section">
        <div class="total-label">TOTAL PRENDAS ENTREGADAS</div>
        <div class="total-value">${totalPrendas}</div>
      </div>
      
      <div class="signature-section">
        <div class="signature-line"></div>
        <div>Firma del Empleado</div>
        <div style="font-size: 10px; margin-top: 5px;">
          Confirmo haber recibido las prendas<br>
          descritas en este comprobante
        </div>
      </div>
      
      <div class="footer">
        <div>Entregado por: ${entregaData.entregadoPor.nombre || 'Sistema'}</div>
        <div style="margin-top: 5px;">Gracias por su confianza</div>
        <div>www.sanimax.com</div>
      </div>
    </body>
    </html>
  `;
};

/**
 * Imprime un recibo de entrega
 */
export const printEntregaReceipt = (entregaData) => {
  const content = generateEntregaReceipt(entregaData);
  
  if (!content) {
    console.error('No se pudo generar el contenido del recibo');
    return;
  }

  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = 'none';
  
  document.body.appendChild(iframe);
  
  iframe.contentWindow.document.open();
  iframe.contentWindow.document.write(content);
  iframe.contentWindow.document.close();
  
  iframe.onload = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    } catch (error) {
      console.error('Error al imprimir:', error);
      document.body.removeChild(iframe);
    }
  };
};

/**
 * Genera un preview del recibo de entrega
 */
export const previewEntregaReceipt = (entregaData) => {
  const content = generateEntregaReceipt(entregaData);
  const previewWindow = window.open('', '_blank', 'width=400,height=600');
  
  if (previewWindow) {
    previewWindow.document.write(content);
    previewWindow.document.close();
  }
};